import React from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { textBlue, text5 } from '@osmosis/tailwind-ui/styles/text';
import {
  bg1,
  bgPrimaryLightest,
  borderColorBlue600,
} from '@osmosis/tailwind-ui/styles';
import { useIntl } from 'react-intl';

import { chapterDomId, TranscriptChapter } from './transcriptHelpers';

type Props = {
  chapters: TranscriptChapter[];
  activeChapterIndex: number;
  isLocked: boolean;
  chipContainerRef: React.RefObject<HTMLDivElement | null>;
  chipRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  onChapterClick: (index: number) => void;
};

/** Horizontally scrollable row of chapter chips; the active one tracks playback. */
export default function ChaptersBar({
  chapters,
  activeChapterIndex,
  isLocked,
  chipContainerRef,
  chipRefs,
  onChapterClick,
}: Props) {
  const intl = useIntl();

  if (!chapters.some((chapter) => chapter.title)) return null;

  return (
    <Flex
      ref={chipContainerRef}
      data-testid="captions-chapters"
      className="w-full overflow-x-auto shrink-0 scrollbar-none border-b pt-1"
    >
      <Flex className="flex-nowrap items-center gap-2 pb-3 px-3">
        <P cn={cn('mr-1 !text-sm whitespace-nowrap', text5)}>
          {intl.formatMessage({
            id: 'learn.captions_transcript.chapters',
            defaultMessage: 'Chapters:',
          })}
        </P>
        {chapters.map((chapter, index) => {
          const isActive = index === activeChapterIndex;
          return chapter.title ? (
            <Button
              key={chapterDomId(index)}
              ref={(node: HTMLButtonElement | null) => {
                chipRefs.current[index] = node;
              }}
              variant="outline"
              rounded
              disabled={isLocked}
              data-testid="captions-chapter-chip"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => onChapterClick(index)}
              className={cn(
                '!text-sm border px-3 py-0.5 text-sm whitespace-nowrap',
                !isLocked
                  ? 'hover:border-blue-600 hover:text-blue-600 hover:bg-blue-100'
                  : '',
                isActive
                  ? cn(borderColorBlue600, textBlue, bgPrimaryLightest)
                  : cn(bg1),
                isActive && isLocked
                  ? 'hover:border-blue-600 hover:text-blue-600 hover:bg-blue-100'
                  : 'font-normal',
              )}
            >
              {chapter.title}
            </Button>
          ) : null;
        })}
      </Flex>
    </Flex>
  );
}
